/**
 * Field Adapters for Taxonomy Custom Fields
 *
 * Transforms custom field data to match what each component expects.
 * Only transforms if the data is NOT already in the correct format.
 */

import { TFeatureList } from "@page-builder/ts/generated";

// ============================================================================
// TYPES
// ============================================================================

// FeatureList item type from generated file
export type FeatureListItem = NonNullable<
    TFeatureList["props"]["items"]
>[number];

// ============================================================================
// TYPE GUARDS - Check if data is already correct for component
// ============================================================================

// All possible keys that a FeatureListItem can have
const FEATURE_LIST_ITEM_KEYS = [
    "title",
    "description",
    "showList",
    "list",
    "note",
    "href",
    "image",
    "button",
] as const;

/**
 * Checks if value is already in FeatureList format.
 * Returns true if the object has at least one FeatureListItem property.
 */
export function isFeatureListItem(value: unknown): value is FeatureListItem {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    // Check if object has at least one FeatureListItem key
    return FEATURE_LIST_ITEM_KEYS.some((key) => key in value);
}

/**
 * Checks if value is already an array of FeatureList items
 */
export function isFeatureListArray(value: unknown): value is FeatureListItem[] {
    return (
        Array.isArray(value) && value.length > 0 && isFeatureListItem(value[0])
    );
}

// ============================================================================
// ADAPTERS - Transform data to correct format for component
// ============================================================================

/**
 * Adapts data to FeatureList item format.
 * If already correct format, returns as-is.
 * If array of objects with 'label', transforms to { title: label }
 */
function adaptForFeatureList(value: unknown): FeatureListItem[] {
    // Already correct format - return as-is
    if (isFeatureListArray(value)) {
        return value;
    }

    // Array of objects - try to transform
    if (Array.isArray(value) && value.length > 0) {
        return value.map((item) => {
            // If item has 'label', use it as title
            if (typeof item === "object" && item !== null && "label" in item) {
                return { title: (item as { label: string }).label };
            }
            // If item is a string, use it as title
            if (typeof item === "string") {
                return { title: item };
            }
            // Return item as-is
            return item as FeatureListItem;
        });
    }

    // Unknown format - return empty array
    return [];
}

function adaptForStandAloneImageTagLabel(value: unknown): string {
    // If value is already a string, return as-is
    if (typeof value === "string") {
        return value;
    }

    // If value is an object with 'label', return that
    if (typeof value === "object" && value !== null && "label" in value) {
        return String((value as { label: unknown }).label);
    }

    // Otherwise, return empty string
    return "";
}

// ============================================================================
// MAIN ADAPTER - Switch based on component ID and prop key
// ============================================================================

/**
 * Adapts custom field data based on which component will consume it.
 * Only transforms if data is NOT already in correct format.
 * Returns data unchanged for components not in switch.
 */
export function adaptForComponent(
    componentId: string,
    propKey: string,
    value: unknown,
): unknown {
    // Check both component AND prop to determine if transformation is needed
    switch (`${componentId}.${propKey}`) {
        case "FeatureList.items":
            // Only transform the 'items' prop for FeatureList
            return adaptForFeatureList(value);
        case "StandAloneImage.tagLabel":
            return adaptForStandAloneImageTagLabel(value);

        // Add more component.prop combinations here as needed:

        default:
            // No matching component.prop - return data unchanged
            return value;
    }
}
